import type { UnifiedOptions } from '@amplitude/unified';
import type { DataGrailConsentCategory } from '@/src/lib/datagrail/types';

export const AMPLITUDE_API_KEY = process.env.NEXT_PUBLIC_AMPLITUDE_API_KEY ?? '';

/**
 * Amplitude is a product-analytics vendor, so it sits in the same DataGrail
 * category as Comscore rather than in `marketing`.
 */
export const AMPLITUDE_CONSENT_CATEGORY: DataGrailConsentCategory = 'performance';

const STYLED_COMPONENTS_HASH = /^(?=[a-zA-Z]{5,8}$)(?=.*[A-Z])/;

/**
 * Options for Session Replay's vendored @medv/finder. Only `maxNumberOfTries` and
 * `threshold` are in the SDK's types; `className` and `attr` reach the finder through
 * its `{ ...defaults, ...options }` merge, so re-check `hooks/click.js` on an SDK bump.
 */
const heatmapSelectorOptions = {
  maxNumberOfTries: 100,
  threshold: 100,
  className: (name: string) => !STYLED_COMPONENTS_HASH.test(name) && !name.startsWith('sc-'),
  attr: (name: string) => name === 'data-testid' || name === 'aria-label',
};

export function getAmplitudeOptions(userId?: string): UnifiedOptions {
  return {
    serverZone: 'US',
    analytics: {
      userId,
      autocapture: {
        attribution: true,
        fileDownloads: true,
        formInteractions: true,
        sessions: true,
        pageViews: {
          // The SRP writes filter state into the query string on every facet
          // change. `'all'` would emit a Page Viewed event — and the network
          // request behind it — inside each of those interactions.
          trackHistoryChanges: 'pathOnly',
        },
        // Each of these installs document-level listeners that do real work per
        // interaction (DOM-path serialization, rage/dead-click timers, fetch
        // patching), which lands inside the INP measurement window. Clickstream
        // already carries HotPads' intent events.
        elementInteractions: false,
        frustrationInteractions: false,
        networkTracking: false,
        performanceTracking: false,
        // Web Vitals already report through rp-bootstrap-browser -> Datadog RUM.
        webVitals: false,
      },
      // Remote config costs a blocking round trip during init and lets the
      // Amplitude UI switch the autocapture options above back on. Config stays
      // in this file so a CWV regression can't arrive without a deploy.
      remoteConfig: { fetchRemoteConfig: false },
    },
    // Session Replay sampling lives in the Amplitude UI: the project's
    // `sr_sampling_config` wins over the rate below, so this is only the fallback
    // for a project with no sampling config at all. It matches the SDK's own
    // default and is restated to keep it from drifting — Session Replay attaches
    // an rrweb recorder (a MutationObserver over the whole document plus periodic
    // serialization) for the life of the session and pulls a multi-hundred-KB
    // chunk to do it, so "Amplitude has no config yet" must not mean "record".
    //
    // With heatmap interaction capture on (a UI-side setting this file cannot turn
    // off), every click runs a CSS-selector finder inside rrweb's document click
    // listener. Seeded from all of a button's 25–36 classes it tries up to a
    // thousand querySelectorAll candidates, a median 254 ms on slow mobile taps.
    // Seeding from test ids and labels and skipping hashed styled-components
    // classes finds a unique selector in a few tries; restating `enabled` keeps
    // the SDK's idle-time compression, which a bare `performanceConfig` replaces.
    sessionReplay: { sampleRate: 0, performanceConfig: { enabled: true, interaction: heatmapSelectorOptions } },
    experiment: {
      // Nothing reads a flag during load, so skip the fetch that init would
      // otherwise fire. Call `experiment().fetch()` when a caller needs one.
      fetchOnStart: false,
    },
    // Guides & Surveys injects overlay UI into a page that has already painted,
    // which is a layout-shift source. Off until a surface actually wants it.
    engagement: { skip: true },
  };
}
